import React, { useEffect, useMemo } from 'react';

/* React Redux */
import { useDispatch, useSelector } from 'react-redux';

/* React router dom */
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/* Material UI */
import { Snackbar, Alert, Typography, useMediaQuery, Box, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid';

/* Project */
import Steps from 'content/features/steps/Steps';
import StyledContainer from 'content/containers/StyledContainer';
import VehicleForm from 'content/features/schedulerSteps/VehicleForm';
import ServiceForm from 'content/features/schedulerSteps/ServiceForm';
import VehicleOwnerStep from 'content/features/schedulerSteps/VehicleOwnerStep';
import DateForm from 'content/features/schedulerSteps/DateForm';
import ResumeView from 'content/features/schedulerSteps/ResumeView';
import { getScheduleStateFromApi, setResetSteps, setView } from 'content/app.actions';
import { clearSchedulerError } from 'content/features/schedulerSteps/schedulerSteps.actions';
import colors from 'utils/colors';
import { controlsChanged } from 'content/features/welcomeScreens/welcomeScreens.actions';

const titles = [
  '1. Datos del Vehículo',
  '2. Datos del Propietario',
  '3. Datos del Servicio',
  '4. Horario del Servicio',
  '5. Resumen del Agendamiento',
];

const formTitles = {
  maintenance: 'Agendar Mantención',
  diagnosis: 'Agendar Diagnóstico',
};

function Scheduler() {
  const reduxActiveStep = useSelector((state) => state.app.activeStep);
  const view = useSelector((state) => state.app.view);
  const error = useSelector((state) => state.schedulerSteps.error);
  const errorMsg = useSelector((state) => state.schedulerSteps.errorMsg);
  const reduxSelectedService = useSelector((state) => state.welcomeScreen.controls.selectedService);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const smUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));

  const { service: urlService, step: urlStep } = useParams();
  const selectedService = reduxSelectedService || urlService;

  const activeStep = urlStep ? parseInt(urlStep, 10) - 1 : (reduxActiveStep || 0);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [activeStep]);
  
  const displayTitle = selectedService && titles[selectedService] 
    ? titles[selectedService] 
    : 'Agendar Servicio';

  const isDirectUrlAccess = location.pathname.includes('/steps/');

  useEffect(() => {
    if (urlService && (!view || view === '')) {
       dispatch(setView({ value: 'schedule' }));
       dispatch(controlsChanged({ name: 'selectedService', value: urlService }));
    }

    if (view === 'schedule' || urlService) {
        dispatch(getScheduleStateFromApi());
    }
  }, [dispatch, view, urlService]);

  useEffect(() => {
    if (view === '' && !isDirectUrlAccess) {
      navigate('/');
    }
  }, [view, isDirectUrlAccess, navigate]);

  useEffect(() => {
    if (selectedService) {
      const queryParams = location.search;
      const targetUrl = `/${selectedService}/steps/${activeStep + 1}${queryParams}`;
      
      if (location.pathname + location.search !== targetUrl) {
         window.history.replaceState({}, '', targetUrl);
      }
    }
  }, [selectedService, activeStep, location.search, location.pathname]);

  useEffect(
    () => () => {
      dispatch(setResetSteps());
    },
    [dispatch],
  );

  const CurrentStepComponent = useMemo(() => {
    const views = [
      <VehicleForm />,
      <VehicleOwnerStep />,
      <ServiceForm />,
      <DateForm />,
      <ResumeView />,
    ];
    return views[activeStep] || null;
  }, [activeStep]);

  const emptyErrors = () => {
    dispatch(clearSchedulerError());
  };

  if (!selectedService) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StyledContainer title={displayTitle}>
      <Steps stepsNumber={6}>
        <Grid
          style={{
            marginTop: 30,
            marginBottom: 30,
            border: `2px solid ${colors.lightBlue}`,
            borderRadius: 24,
          }}>
          <Typography
            style={{
              paddingLeft: 16,
              paddingTop: 16,
              paddingBottom: 8,
              marginBottom: 24,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
            }}
            bgcolor={colors.lightBlue}
            color={colors.white}
            variant="h6">
            {titles[activeStep] || ''}
          </Typography>
          <Grid sx={{ paddingX: smUp ? 8 : 2 }}>{CurrentStepComponent}</Grid>
        </Grid>
      </Steps>
      <Snackbar
        open={error}
        onClose={emptyErrors}
        autoHideDuration={9000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error" sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </StyledContainer>
  );
}

export default Scheduler;
